import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';

export interface UserPermissions {
    catalog?: boolean;
    inventory?: boolean;
    orders?: boolean;
    coupons?: boolean;
    stats?: boolean;
    allow_video?: boolean;
    allow_variants?: boolean;
    allow_marketing?: boolean;
    manage_stock?: boolean;
    subscription_plan?: string;
    subscription_status?: string;
    role?: string;
    is_super_admin?: boolean;
    [key: string]: boolean | string | undefined;
}

const defaultPermissions: UserPermissions = {
    catalog: true,
    inventory: true,
    orders: true,
    coupons: true,
    stats: true,
    allow_video: false,
    allow_variants: true,
    allow_marketing: true,
    manage_stock: true,
    subscription_plan: 'basic',
    subscription_status: 'active',
    role: 'owner',
    is_super_admin: false
};

export function usePermissions() {
    const { user } = useAuth();
    const [permissions, setPermissions] = useState<UserPermissions>(defaultPermissions);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;

        async function fetchPermissions() {
            if (!user) {
                if (mounted) {
                    setPermissions(defaultPermissions);
                    setLoading(false);
                }
                return;
            }

            try {
                // 1. Intentar cargar como dueño directo
                let { data, error } = await supabase
                    .from('business_profiles')
                    .select('permissions, subscription_plan, subscription_status, is_super_admin')
                    .eq('id', user.id)
                    .maybeSingle();

                let role = 'owner';
                let isSuperAdmin = data?.is_super_admin || false;

                // 2. Si no es dueño, verificar si es empleado
                if (!data) {
                    const { data: memberData } = await supabase
                        .from('team_members')
                        .select('business_id, role')
                        .eq('user_id', user.id)
                        .eq('status', 'active')
                        .maybeSingle();

                    if (memberData && memberData.business_id) {
                        role = memberData.role; // Usar el rol real del empleado (waiter, cashier, etc)

                        // Cargar permisos del negocio del jefe
                        const { data: businessData } = await supabase
                            .from('business_profiles')
                            .select('permissions, subscription_plan, subscription_status, is_super_admin')
                            .eq('id', memberData.business_id)
                            .maybeSingle();

                        if (businessData) {
                            data = businessData;
                            // Los empleados NO son super admin aunque el jefe lo sea (seguridad)
                            isSuperAdmin = false;
                        }
                    }
                }

                if (data && mounted) {
                    let parsedPermissions = {};
                    if (data.permissions) {
                        if (typeof data.permissions === 'string') {
                            try { parsedPermissions = JSON.parse(data.permissions); } catch (e) {}
                        } else {
                            parsedPermissions = data.permissions;
                        }
                    }

                    const finalPermissions: UserPermissions = {
                        ...defaultPermissions,
                        ...parsedPermissions, // Cast seguro ahora que está parseado
                        subscription_plan: data.subscription_plan,
                        subscription_status: data.subscription_status,
                        role: role,
                        is_super_admin: isSuperAdmin
                    };

                    setPermissions(finalPermissions);
                } else if (mounted) {
                    // Si no tiene negocio ni empleo, dejar defaults (basic)
                    setPermissions(defaultPermissions);
                }
            } catch (err) {
                console.error(err);
                if (mounted) setPermissions(defaultPermissions);
            } finally {
                if (mounted) setLoading(false);
            }
        }

        fetchPermissions();

        // Determinar el ID a escuchar (si es empleado, escuchar al jefe)
        let businessIdToListen = user?.id;
        
        // Esta es una simplificación, en fetchPermissions ya lo calculamos
        // Pero para la suscripción necesitamos saberlo fuera o re-suscribir
        
        const setupSubscription = async () => {
            let idToListen = user?.id;
            
            // Verificar si es empleado para escuchar al negocio correcto
            const { data: memberData } = await supabase
                .from('team_members')
                .select('business_id')
                .eq('user_id', user?.id)
                .eq('status', 'active')
                .maybeSingle();
                
            if (memberData?.business_id) {
                idToListen = memberData.business_id;
            }

            if (!idToListen) return null;

            const channel = supabase
                .channel(`public:business_profiles:id=eq.${idToListen}`)
                .on(
                    'postgres_changes',
                    {
                        event: 'UPDATE',
                        schema: 'public',
                        table: 'business_profiles',
                        filter: `id=eq.${idToListen}`
                    },
                    () => {
                        console.log('🔄 Permissions updated in real-time');
                        fetchPermissions();
                    }
                )
                .subscribe();
                
            return channel;
        };

        let activeChannel: any = null;
        setupSubscription()
            .then(channel => {
                activeChannel = channel;
            })
            .catch(error => {
                console.error('Error setting up permissions subscription:', error);
            });

        return () => {
            mounted = false;
            if (activeChannel) {
                supabase.removeChannel(activeChannel);
            }
        };
    }, [user]);

    // Helper to check access. Returns true if permission is explicitly true or undefined (default allow)
    // Returns false ONLY if explicitly set to false.
    const canAccess = (feature: keyof UserPermissions) => {
        // If loading, maybe block or allow? defaulting to allow to avoid flicker
        return permissions[feature] !== false;
    };

    return { permissions, loading, canAccess };
}
